import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import { CampaignTemplatePanel } from "../components/CampaignTemplatePanel";
import { TacticEditor } from "../components/TacticEditor";
import { EmptyCard, QueryErrorState, TabHeading, TabSkeleton } from "../components/tab-states";
import type { TacticDefinition } from "../data/cockpit-data";
import { useTactics } from "../data/queries";

type EditorState =
  | { open: false }
  | { open: true; mode: "create" | "edit" | "duplicate"; tactic: TacticDefinition | null };

/** The versioned intervention definitions, and the campaign shapes they use. */
export function TacticsTab() {
  const tactics = useTactics();
  const queryClient = useQueryClient();
  const [editor, setEditor] = useState<EditorState>({ open: false });
  const [success, setSuccess] = useState("");
  const rows = tactics.data ?? [];

  async function saved(message: string) {
    setSuccess(message);
    setEditor({ open: false });
    await queryClient.invalidateQueries({ queryKey: ["machine", "tactics"] });
  }

  if (editor.open) {
    return (
      <div className="flex flex-col gap-4">
        <TabHeading
          title="Tactic definition"
          description="Catalogue-driven controls keep intervention parameters within the shipped W4 contract."
        />
        <TacticEditor
          tactic={editor.tactic}
          mode={editor.mode}
          onCancel={() => setEditor({ open: false })}
          onSaved={saved}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <TabHeading
        title="Tactics"
        description="Versioned intervention definitions and the signals that use them. Duplicate a template to test a variant."
        aside={
          <Button
            onClick={() => {
              setSuccess("");
              setEditor({ open: true, mode: "create", tactic: null });
            }}
          >
            Add tactic
          </Button>
        }
      />
      {success && (
        <p role="status" className="text-sm font-medium text-ks-ink">
          {success}
        </p>
      )}
      {tactics.isPending ? (
        <TabSkeleton />
      ) : tactics.isError ? (
        <QueryErrorState title="Tactics unavailable" onRetry={() => tactics.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyCard label="No tactics yet" />
      ) : (
        <div className="flex flex-col gap-3">
          {rows.map((tactic) => (
            <TacticCard
              key={tactic.tactic_id}
              tactic={tactic}
              onEdit={() => {
                setSuccess("");
                setEditor({ open: true, mode: "edit", tactic });
              }}
              onDuplicate={() => {
                setSuccess("");
                setEditor({ open: true, mode: "duplicate", tactic });
              }}
            />
          ))}
        </div>
      )}
      <CampaignTemplatePanel onSaved={setSuccess} />
    </div>
  );
}

function TacticCard({
  tactic,
  onEdit,
  onDuplicate,
}: {
  tactic: TacticDefinition;
  onEdit: () => void;
  onDuplicate: () => void;
}) {
  const current = tactic.versions.find((version) => version.is_current) ?? tactic.versions[0];
  const createCampaign = current?.interventions.find((item) => item.intervention_type === "create_campaign");
  const campaignTemplate =
    typeof createCampaign?.params.template === "string" && createCampaign.params.template
      ? createCampaign.params.template
      : "Not configured";

  return (
    <Card>
      <CardContent className="flex flex-col gap-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-[15px] font-semibold text-ks-ink">{tactic.name}</h3>
              <Badge variant="outline">{tactic.status}</Badge>
            </div>
            <p className="mt-1 font-mono text-xs text-muted-foreground">{tactic.tactic_id}</p>
            {tactic.description && <p className="mt-3 text-sm text-muted-foreground">{tactic.description}</p>}
          </div>
          <div className="flex flex-none flex-wrap gap-2">
            <Button variant="outline" size="lg" onClick={onDuplicate}>
              Duplicate
            </Button>
            <Button variant="outline" size="lg" onClick={onEdit}>
              Edit definition
            </Button>
          </div>
        </div>
        <dl
          className={`grid gap-4 border-t border-ks-grey-3 pt-4 ${createCampaign ? "sm:grid-cols-4" : "sm:grid-cols-3"}`}
        >
          <Metric label="Current version" value={`v${tactic.current_version}`} />
          <Metric label="Version count" value={String(tactic.version_count)} />
          <Metric
            label="Where used"
            value={tactic.signal_ids.length ? tactic.signal_ids.join(", ") : "No signal link returned"}
          />
          {createCampaign && <Metric label="Campaign template" value={campaignTemplate} />}
        </dl>
        {current && (
          <Accordion type="single" collapsible>
            <AccordionItem value="versions">
              <AccordionTrigger>Version history ({tactic.versions.length})</AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2">
                  {tactic.versions.map((version) => (
                    <div
                      key={version.version}
                      className="flex flex-wrap items-center justify-between gap-2 border-t border-ks-grey-3 pt-2 text-xs text-muted-foreground"
                    >
                      <span className="font-semibold text-ks-ink">
                        v{version.version}
                        {version.is_current ? " (current)" : ""}
                      </span>
                      <span>
                        {version.interventions.map((item) => item.intervention_type).join(", ") ||
                          "No interventions"}
                      </span>
                      <span>{version.created_by ?? "Unknown author"}</span>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-sm font-semibold text-ks-ink">{value}</dd>
    </div>
  );
}
